import { useState } from 'react';

import { type HabitSuggestion, tagLabel } from '@/store/proactive/proactive.mappers';

import { ActionsButton } from '../ActionsButton/ActionsButton';
import { Button, ButtonOutlined } from '../Button';
import ContextMenu from '../ContextMenu/ContextMenu';
import Modal from '../Modal/Modal';
import { StatusPill } from '../StatusPill/StatusPill';

import s from './HolidaySuggestionModal.module.scss';
import { reminderOptions } from './reminderOptions';

type HabitSuggestionModalProps = {
  suggestion: HabitSuggestion | null;
  onClose: () => void;
  onPlan: () => void;
  /** Skip this round only — the routine comes back next time. */
  onSkip: () => void;
  /** Remind at the moment the person picked. */
  onRemindLater: (until: Date) => void;
  /** "Don't suggest this again" (topic) or "Turn off habit suggestions" (kind). */
  onMute: (scope: 'topic' | 'kind') => void;
  /** A reaction is in flight — the buttons wait for it. */
  busy?: boolean;
};

const formatDay = (date: Date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/**
 * A routine of the vessel's coming round again — the same place, the same
 * people or the same kind of outing — offered a few days ahead: Plan it (opens
 * the event wizard), Not this time (skip this round), Remind me later; muting
 * lives in the ⋯ menu, ✕ just closes. The people who usually come are named
 * here and picked in the wizard, never invited on their own. Purely
 * presentational, like HolidaySuggestionModal.
 *
 * @alias habit suggestion, routine suggestion, ai idea
 */
export const HabitSuggestionModal = ({
  suggestion,
  onClose,
  onPlan,
  onSkip,
  onRemindLater,
  onMute,
  busy = false,
}: HabitSuggestionModalProps) => {
  // The menu's anchor lives in state (not a ref read during render).
  const [remindAnchor, setRemindAnchor] = useState<HTMLElement | null>(null);
  const remindOpen = Boolean(remindAnchor);
  if (!suggestion) return null;
  const reminders = reminderOptions(suggestion);
  const guests = suggestion.suggestedGuests.map((guest) => guest.name);

  return (
    <Modal open onClose={onClose} title="AI Idea">
      <div className={s.body}>
        <p className={s.eyebrow}>
          <span className={s.mark} aria-hidden="true">
            ✦
          </span>
          Your routine · {formatDay(suggestion.date)}
        </p>

        <h3 className={s.title}>{suggestion.title}</h3>
        {suggestion.description && <p className={s.description}>{suggestion.description}</p>}

        {suggestion.tags.length > 0 && (
          <ul className={s.tags} aria-label="Tags">
            {suggestion.tags.map((tag) => (
              <li key={tag}>
                <StatusPill label={tagLabel(tag)} tone="info" />
              </li>
            ))}
          </ul>
        )}

        {guests.length > 0 && <p className={s.description}>Usually with {guests.join(', ')}</p>}

        {suggestion.reason && <p className={s.reason}>{suggestion.reason}</p>}

        <div className={s.actions}>
          <ActionsButton
            ariaLabel="More Options"
            className={s.more}
            items={[
              { label: 'Don’t Suggest This Again', onClick: () => onMute('topic') },
              { label: 'Turn Off Habit Suggestions', onClick: () => onMute('kind') },
            ]}
          />
          <ButtonOutlined type="button" onClick={onSkip} disabled={busy}>
            Skip This Time
          </ButtonOutlined>
          {reminders.length > 0 && (
            <ButtonOutlined
              type="button"
              onClick={(event) => {
                const target = event.currentTarget;
                setRemindAnchor((prev) => (prev ? null : target));
              }}
              disabled={busy}
              aria-haspopup="menu"
              aria-expanded={remindOpen}
            >
              Remind Me Later
            </ButtonOutlined>
          )}
          <Button type="button" onClick={onPlan} disabled={busy}>
            Plan Event
          </Button>
        </div>
        <ContextMenu
          isOpen={remindOpen}
          onClose={() => setRemindAnchor(null)}
          anchorEl={remindAnchor}
          items={reminders.map(({ label, until }) => ({
            label,
            onClick: () => {
              setRemindAnchor(null);
              onRemindLater(until);
            },
          }))}
        />
      </div>
    </Modal>
  );
};

export default HabitSuggestionModal;
